import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { Plus, Ruler, Trash2 } from 'lucide-react'
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { applyServerErrors } from '@/components/forms/applyServerErrors'
import { FormField } from '@/components/forms/FormField'
import { toast } from '@/components/feedback/toast-store'
import { Alert } from '@/components/ui/Alert'
import { Button, IconButton } from '@/components/ui/Button'
import { Checkbox } from '@/components/ui/Checkbox'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { FormDialog } from '@/features/academic/components/FormDialog'
import { ToggleField } from '@/features/academic/components/ToggleField'
import { gradingTemplatesQuery, useSaveGradingScale } from '../api'
import {
  coverageProblem,
  gradingScaleSchema,
  type GradingScaleFormInput,
  type GradingScaleFormOutput,
} from '../schemas'
import type { GradeBand, GradingScale } from '../types'

const FORM_ID = 'grading-scale-form'
const FIELDS = ['name', 'isDefault', 'bands', 'outOf'] as const

const toInput = (b: GradeBand) => ({
  label: b.label,
  minPercent: String(b.minPercent),
  maxPercent: String(b.maxPercent),
  gradePoint: b.gradePoint === null ? '' : String(b.gradePoint),
  isPass: b.isPass,
})

interface GradingScaleDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  scale?: GradingScale | null
}

export function GradingScaleDialog({ open, onOpenChange, scale }: GradingScaleDialogProps) {
  const { t } = useTranslation('exams')
  const save = useSaveGradingScale()
  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      icon={<Ruler />}
      size="lg"
      title={scale ? t('Edit {{name}}', { name: scale.name }) : t('New grading scale')}
      description={t(
        'Grades are looked up by percentage. Together the ranges must cover 0–100% with no gaps or overlaps.',
      )}
      formId={FORM_ID}
      submitLabel={scale ? t('Save scale') : t('Create scale')}
      pending={save.isPending}
    >
      <GradingScaleForm
        scale={scale ?? null}
        onSubmit={async (values) => {
          await save.mutateAsync({ ...values, id: scale?.id })
          toast.success(scale ? t('Grading scale updated') : t('Grading scale created'), values.name)
          onOpenChange(false)
        }}
      />
    </FormDialog>
  )
}

function GradingScaleForm({
  scale,
  onSubmit,
}: {
  scale: GradingScale | null
  onSubmit: (v: GradingScaleFormOutput) => Promise<void>
}) {
  const { t } = useTranslation('exams')
  const templates = useQuery({ ...gradingTemplatesQuery(), enabled: scale === null })
  const {
    register,
    control,
    handleSubmit,
    setError,
    reset,
    getValues,
    formState: { errors },
  } = useForm<GradingScaleFormInput, unknown, GradingScaleFormOutput>({
    resolver: zodResolver(gradingScaleSchema),
    defaultValues: {
      name: scale?.name ?? '',
      isDefault: scale?.isDefault ?? false,
      resultMode: scale?.outOf ? 'outOf' : 'percentage',
      outOf: String(scale?.outOf ?? 20),
      bands: scale
        ? scale.bands.map(toInput)
        : [
            toInput({ label: t('Pass'), minPercent: 40, maxPercent: 100, gradePoint: null, isPass: true }),
            toInput({ label: t('Fail'), minPercent: 0, maxPercent: 39.99, gradePoint: null, isPass: false }),
          ],
    },
  })
  const { fields, append, remove } = useFieldArray({ control, name: 'bands' })
  const bands = useWatch({ control, name: 'bands' })
  const resultMode = useWatch({ control, name: 'resultMode' })
  const live = coverageProblem(
    bands.map((b) => ({ minPercent: Number(b.minPercent) || 0, maxPercent: Number(b.maxPercent) || 0 })),
  )
  const bandsError = errors.bands?.message ?? errors.bands?.root?.message

  const submit = handleSubmit(async (values) => {
    try {
      await onSubmit(values)
    } catch (e) {
      applyServerErrors(e, setError, FIELDS)
    }
  })

  return (
    <form id={FORM_ID} onSubmit={(e) => void submit(e)} noValidate className="space-y-5">
      {scale === null && templates.data?.length ? (
        <FormField
          label={t('Start from a template')}
          hint={t('Replaces the grades below; you can adjust them afterwards.')}
        >
          <Select
            placeholder={t('Choose a template…')}
            options={templates.data.map((tpl) => ({ value: tpl.key, label: tpl.name }))}
            onChange={(e) => {
              const tpl = templates.data.find((x) => x.key === e.target.value)
              if (tpl)
                reset({
                  name: getValues('name') || tpl.name,
                  isDefault: getValues('isDefault'),
                  bands: tpl.bands.map(toInput),
                  resultMode: tpl.outOf ? 'outOf' : 'percentage',
                  outOf: String(tpl.outOf ?? 20),
                })
            }}
          />
        </FormField>
      ) : null}
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField label={t('Name')} error={errors.name?.message} required>
          <Input autoComplete="off" placeholder={t('e.g. Letter A–F')} {...register('name')} />
        </FormField>
        <Controller
          control={control}
          name="isDefault"
          render={({ field }) => (
            <ToggleField
              className="self-end rounded-control border border-line bg-surface-muted/60 p-3"
              label={t('School default')}
              description={t('Used for new exams unless another scale is chosen.')}
              checked={field.value}
              onCheckedChange={field.onChange}
              disabled={scale?.isDefault}
            />
          )}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <FormField
          label={t('Show results as')}
          hint={
            resultMode === 'outOf'
              ? t('Marks out of N; the overall result is the average weighted by subject coefficients.')
              : t('Total marks as a percentage of the maximum.')
          }
        >
          <Select
            options={[
              { value: 'percentage', label: t('Percentage') },
              { value: 'outOf', label: t('Marks out of N (e.g. /20)') },
            ]}
            {...register('resultMode')}
          />
        </FormField>
        {resultMode === 'outOf' ? (
          <FormField
            label={t('Out of')}
            error={errors.outOf?.message}
            hint={t('Grades below are still looked up by percentage: 50% = 10/20.')}
            required
          >
            <Input inputMode="numeric" className="w-24 text-right tabular" {...register('outOf')} />
          </FormField>
        ) : null}
      </div>

      <fieldset>
        <legend className="mb-2 text-[0.8125rem] font-medium text-ink-soft">{t('Grades')}</legend>
        <div className="overflow-x-auto rounded-control border border-line">
          <table className="w-full text-sm">
            <thead className="bg-surface-muted text-left text-2xs font-semibold tracking-wider text-muted uppercase">
              <tr>
                <th scope="col" className="px-3 py-2">
                  {t('Grade')}
                </th>
                <th scope="col" className="px-3 py-2">
                  {t('From %')}
                </th>
                <th scope="col" className="px-3 py-2">
                  {t('To %')}
                </th>
                <th scope="col" className="px-3 py-2">
                  {t('Grade point')}
                </th>
                <th scope="col" className="px-3 py-2 text-center">
                  {t('Pass')}
                </th>
                <th scope="col" className="px-3 py-2">
                  <span className="sr-only">{t('Remove')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {fields.map((f, i) => {
                const e = errors.bands?.[i]
                return (
                  <tr key={f.id} className="border-t border-line align-top">
                    <td className="px-3 py-2">
                      <Input
                        size="sm"
                        aria-label={t('Grade {{n}} label', { n: i + 1 })}
                        aria-invalid={e?.label ? true : undefined}
                        className="w-20"
                        {...register(`bands.${i}.label`)}
                      />
                      {e?.label ? (
                        <p className="mt-1 text-2xs text-danger-600">{t(e.label.message ?? '')}</p>
                      ) : null}
                    </td>
                    <td className="px-3 py-2">
                      <Input
                        size="sm"
                        inputMode="decimal"
                        aria-label={t('Grade {{n}} lower bound', { n: i + 1 })}
                        aria-invalid={e?.minPercent ? true : undefined}
                        className="w-24 text-right tabular"
                        {...register(`bands.${i}.minPercent`)}
                      />
                      {e?.minPercent ? (
                        <p className="mt-1 text-2xs text-danger-600">{t(e.minPercent.message ?? '')}</p>
                      ) : null}
                    </td>
                    <td className="px-3 py-2">
                      <Input
                        size="sm"
                        inputMode="decimal"
                        aria-label={t('Grade {{n}} upper bound', { n: i + 1 })}
                        aria-invalid={e?.maxPercent ? true : undefined}
                        className="w-24 text-right tabular"
                        {...register(`bands.${i}.maxPercent`)}
                      />
                      {e?.maxPercent ? (
                        <p className="mt-1 text-2xs text-danger-600">{t(e.maxPercent.message ?? '')}</p>
                      ) : null}
                    </td>
                    <td className="px-3 py-2">
                      <Input
                        size="sm"
                        inputMode="decimal"
                        placeholder="—"
                        aria-label={t('Grade {{n}} grade point', { n: i + 1 })}
                        className="w-20 text-right tabular"
                        {...register(`bands.${i}.gradePoint`)}
                      />
                      {e?.gradePoint ? (
                        <p className="mt-1 text-2xs text-danger-600">{t(e.gradePoint.message ?? '')}</p>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <Controller
                        control={control}
                        name={`bands.${i}.isPass`}
                        render={({ field }) => (
                          <Checkbox
                            className="mt-1.5"
                            aria-label={t('Grade {{n}} is a pass', { n: i + 1 })}
                            checked={field.value}
                            onCheckedChange={(v) => field.onChange(v === true)}
                          />
                        )}
                      />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <IconButton
                        label={t('Remove grade {{n}}', { n: i + 1 })}
                        icon={<Trash2 />}
                        size="icon-sm"
                        disabled={fields.length <= 1}
                        onClick={() => remove(i)}
                      />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Plus />}
            disabled={fields.length >= 20}
            onClick={() =>
              append({ label: '', minPercent: '', maxPercent: '', gradePoint: '', isPass: true })
            }
          >
            {t('Add grade')}
          </Button>
          <p className="text-xs text-muted">{t('Ranges are inclusive, e.g. 70–79.99 then 80–100.')}</p>
        </div>
        {bandsError ? (
          <Alert tone="danger" className="mt-3">
            {t(bandsError)}
          </Alert>
        ) : live ? (
          <Alert tone="warning" className="mt-3">
            {live}
          </Alert>
        ) : (
          <Alert tone="success" className="mt-3">
            {t('The grades cover 0–100% exactly.')}
          </Alert>
        )}
      </fieldset>
    </form>
  )
}
